import { useState } from "react";
import type { ItemRow } from "../../contracts/index";

export function useBacklogSelection(rows: readonly ItemRow[], filterKey: string) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [pickedFailed, setPickedFailed] = useState<Map<string, boolean>>(() => new Map());
  // DI-120: "all matching" belongs to the filter it was chosen under; another filter falls back to the picked rows.
  const [matchingFor, setMatchingFor] = useState<string | null>(null);
  const allMatching = matchingFor === filterKey;
  const toggle = (row: ItemRow) => {
    const next = new Set(picked);
    const failed = new Map(pickedFailed);
    if (next.has(row.id)) {
      next.delete(row.id);
      failed.delete(row.id);
    } else {
      next.add(row.id);
      failed.set(row.id, row.status.status === "failed");
    }
    setPicked(next);
    setPickedFailed(failed);
    setMatchingFor(null);
  };
  const loaded = rows.filter((r) => picked.has(r.id)).length;
  const allLoaded = rows.length > 0 && loaded === rows.length;
  const clear = () => {
    setPicked(new Set());
    setPickedFailed(new Map());
    setMatchingFor(null);
  };
  const toggleLoaded = () => {
    if (allLoaded || allMatching) {
      clear();
      return;
    }
    setPicked(new Set([...picked, ...rows.map((r) => r.id)]));
    setPickedFailed(
      new Map([...pickedFailed, ...rows.map((r): [string, boolean] => [r.id, r.status.status === "failed"])]),
    );
  };
  const failed = [...pickedFailed.values()].filter(Boolean).length;
  const checked = allMatching ? new Set([...picked, ...rows.map((r) => r.id)]) : picked;
  return {
    picked,
    checked,
    failed,
    allLoaded,
    allMatching,
    toggle,
    clear,
    toggleLoaded,
    selectAllMatching: () => {
      setMatchingFor(filterKey);
    },
    selectLoaded: () => {
      setMatchingFor(null);
    },
  };
}

export type BacklogSelection = ReturnType<typeof useBacklogSelection>;
